<script setup lang="ts">
  import { ref, computed } from "vue";
  import { useI18n } from "vue-i18n";
  import { Text } from "@ui-kit";
  import { NumberService } from "@src/services";

  const { title, amount, quantity, max_amount, max_quantity, is_zone, is_link, selected_display_graph_id } =
    defineProps<{
      title: string;
      amount: string;
      quantity: number;
      max_amount: number;
      max_quantity: number;
      is_zone: boolean;
      is_link: boolean;
      selected_display_graph_id: string;
    }>();

  const show_amount = computed(() => selected_display_graph_id === "all" || selected_display_graph_id === "amount");
  const show_quantity = computed(() => selected_display_graph_id === "all" || selected_display_graph_id === "quantity");

  const i18n = useI18n();

  const content_ref = ref<HTMLDivElement>(null);
  const computed_title = computed(() => {
    if (is_zone) return title;
    if (is_link) return i18n.t("Продажи");

    return i18n.t("Вне зоны");
  });

  const getWidth = (cur: number, max: number) => {
    if (!content_ref.value) return "";
    if (cur === 0) return "1px";

    const width = content_ref.value.clientWidth - 68;

    return (cur * width) / max + "px";
  };
</script>

<template>
  <div class="moneymap__item-chart" data-testid="moneymap-item-chart">
    <div class="moneymap__item-chart__title">
      <Text color="secondary">{{ computed_title }}</Text>
    </div>
    <div ref="content_ref" class="moneymap__item-chart__content">
      <div v-if="show_amount" class="moneymap__item-chart__amount" data-testid="moneymap-chart-amount">
        <hr class="moneymap__item-chart__graph" :style="{ 'width': getWidth(Number.parseInt(amount), max_amount) }" />
        <div class="moneymap__item-chart__count">
          <Text font="body-regular-12" :wrap="false" data-testid="moneymap-item-chart-amount">
            {{ NumberService.toLocaleCurrency(amount, true) }}
          </Text>
        </div>
      </div>
      <div v-if="show_quantity" class="moneymap__item-chart__quantity" data-testid="moneymap-chart-quantity">
        <hr class="moneymap__item-chart__graph" :style="{ 'width': getWidth(quantity, max_quantity) }" />
        <div class="moneymap__item-chart__count">
          <Text font="body-regular-12" :wrap="false" data-testid="moneymap-item-chart-quantity">
            {{ `${quantity} ${$t("шт")}` }}
          </Text>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .moneymap__item-chart {
    display: flex;
    gap: 10px;
  }

  .moneymap__item-chart__title {
    width: 56px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: right;
    line-height: 1;

    & :deep(.text) {
      font-weight: 580;
      font-size: 12px;
      letter-spacing: 0.12px;
      text-align: right;
      line-height: inherit;
    }
  }

  .moneymap__item-chart__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    gap: 3px;
  }

  .moneymap__item-chart__quantity,
  .moneymap__item-chart__amount {
    display: flex;
    gap: 8px;
  }

  .moneymap__item-chart__amount .moneymap__item-chart__graph {
    background-color: #1998ff;
  }

  .moneymap__item-chart__quantity .moneymap__item-chart__graph {
    background-color: #7a45e6;
  }

  .moneymap__item-chart__graph-wrapper {
    display: flex;
    flex: 1;
  }

  .moneymap__item-chart__graph {
    border-radius: 4px;
    height: 16px;
  }

  .moneymap__item-chart__count {
    display: flex;
    flex-shrink: 0;
    width: 60px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    & :deep(.text) {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .moneymap__item-not-data {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--sf-base-background-secondary);
    text-align: center;
    padding: 4px;
    border-radius: 6px;

    & :deep(.text) {
      display: block;
      line-height: 13.5px;
    }
  }
</style>
